export interface LevelProgress {
  unlocked: boolean;
  highScore: number;
  completed: boolean;
}

export interface GameProgress {
  levels: LevelProgress[];
  selectedDifficulty: string;
}

const PROGRESS_KEY = 'morse-hero-progress';
const TOTAL_LEVELS = 10;

function createDefaultProgress(): GameProgress {
  const levels: LevelProgress[] = [];
  for (let i = 0; i < TOTAL_LEVELS; i++) {
    levels.push({
      unlocked: i === 0,
      highScore: 0,
      completed: false,
    });
  }
  return { levels, selectedDifficulty: 'easy' };
}

export function loadProgress(): GameProgress {
  try {
    const saved = localStorage.getItem(PROGRESS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const defaults = createDefaultProgress();
      // Merge with defaults to handle new levels being added
      const levels = defaults.levels.map((def, i) => parsed.levels[i] ?? def);
      return {
        levels,
        selectedDifficulty: parsed.selectedDifficulty ?? defaults.selectedDifficulty,
      };
    }
  } catch (e) {
    console.warn('Failed to load progress:', e);
  }
  return createDefaultProgress();
}

export function saveProgress(progress: GameProgress): void {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch (e) {
    console.warn('Failed to save progress:', e);
  }
}

export function resetProgress(): GameProgress {
  try {
    localStorage.removeItem(PROGRESS_KEY);
  } catch (e) {
    console.warn('Failed to reset progress:', e);
  }
  return createDefaultProgress();
}

export function updateLevelProgress(
  levelIndex: number,
  score: number,
  completed: boolean
): { newHighScore: boolean; unlockedLevel: number | null } {
  const progress = loadProgress();
  const level = progress.levels[levelIndex];

  if (!level) {
    return { newHighScore: false, unlockedLevel: null };
  }

  const newHighScore = score > level.highScore;
  if (newHighScore) {
    level.highScore = score;
  }

  let unlockedLevel: number | null = null;

  // If completed for the first time, unlock next level
  if (completed && !level.completed) {
    level.completed = true;
    const nextIndex = levelIndex + 1;
    if (nextIndex < progress.levels.length && !progress.levels[nextIndex].unlocked) {
      progress.levels[nextIndex].unlocked = true;
      unlockedLevel = nextIndex;
    }
  }

  saveProgress(progress);

  return { newHighScore, unlockedLevel };
}

export function isLevelUnlocked(levelIndex: number): boolean {
  const progress = loadProgress();
  return progress.levels[levelIndex]?.unlocked ?? false;
}

export function getLevelHighScore(levelIndex: number): number {
  const progress = loadProgress();
  return progress.levels[levelIndex]?.highScore ?? 0;
}

export function isLevelCompleted(levelIndex: number): boolean {
  const progress = loadProgress();
  return progress.levels[levelIndex]?.completed ?? false;
}

export function getSelectedDifficulty(): string {
  const progress = loadProgress();
  return progress.selectedDifficulty;
}

export function setSelectedDifficulty(difficulty: string): void {
  const progress = loadProgress();
  progress.selectedDifficulty = difficulty;
  saveProgress(progress);
}
